"use client";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
	faBook,
	faChessKnight,
	faDice,
	faDragon,
	faEnvelope,
	faHatWizard,
	faScroll,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

const AboutPage = () => {
	const [showCopyNotification, setShowCopyNotification] = useState(false);

	const contactMethods = [
		{
			id: "email",
			title: "Pergamino Arcano",
			icon: faEnvelope,
			description:
				"Comunícate con nosotros a través del plano etéreo. Respondemos con la velocidad de un hechizo de mensajería.",
			action: "Enviar pergamino",
			link: "mailto:somos@losamosdelcalabozo.com?subject=Queridos Amos del Calabozo",
			color: "bg-blue-600 hover:bg-blue-700",
			hoverEffect: {
				scale: 1.05,
				boxShadow: "0 0 15px #3b82f6",
			},
		},
		{
			id: "whatsapp",
			title: "Cristal de Comunicación",
			icon: faWhatsapp,
			description: "Para mensajes instantáneos entre reinos y dimensiones mágicas.",
			action: "Comunicar por cristal",
			link: `https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, quiero saber más sobre vuestros calabozos y mazmorras. ¿Podéis guiarme en mi aventura?")}`,
			color: "bg-green-600 hover:bg-green-700",
			hoverEffect: {
				scale: 1.05,
				boxShadow: "0 0 15px #22c55e",
			},
		},
	];

	const dungeomMasters = [
		{
			id: "sergio",
			name: "Sergio",
			title: "Fundador de Los Amos del Calabozo",
			avatar: "/images/lo-amos/sergio.webp", // Placeholder - would need actual image
			description:
				"Llevo jugando al rol desde la adolescencia. Siempre tuve un interés en Dragones y Mazmorras por los anuncios en la revista Fangoria de mi hermano, y en cuanto encontré un grupo dispuesto a jugar al rol en mi colegio me lancé a la aventura. Mis primeros juegos fueron Fanhunter, Stormbringer y Vampiro: La Mascarada, y aunque ahora prefiero los sistemas narrativos como Blades in the Dark de John Harper, sin duda mi juego más rodado es Dragones y Mazmorras.",
			favoriteSystems: [
				"Dragones y Mazmorras",
				"Pequeños Detectives de Monstruos",
				"Blades in the Dark",
				"Láseres & Sentimientos",
				"Agon",
				"DRAGON",
				"Mutants & Masterminds",
			],
			otherSystems: [
				"Fanhunter",
				"Pathfinder",
				"Warhammer Fantasy",
				"Dark Heresy",
				"Magissa",
				"Clásicos del Mazmorreo",
			],
			additionalInfo:
				"He dirigido innumerable partidas de un sólo día, y numerosas campañas de años de duración, desde partidas con un solo jugador, a grupos de 20 personas, adultos y niños. También me encantan los juegos de mesa y fui adicto a Heroclix durante años, llegando a ganar campeonatos locales.",
			tagline:
				"Dirijo a cualquier cosa que me propongáis. A una gran parte ya se jugar, pero si aún no sé puedo hacerme con el sistema en poco tiempo.",
			icon: faHatWizard,
		},
		{
			id: "bez",
			name: "Bez",
			title: "Maestro Internacional",
			avatar: "/images/lo-amos/bez.webp", // Placeholder - would need actual image
			description:
				"Llevo jugando al rol desde que tenía doce años, siendo introducido al hobby a través de librojuegos y juegos de miniaturas de guerra. Me uní a un club local lleno de estudiantes universitarios, jugando y dirigiendo con ellos hasta que me fui a la universidad. Mientras estaba en la universidad, no solo jugaba y dirigía juegos de rol, sino que también fui director de La Mascarilla, un juego de rol coordinado a nivel mundial.",
			background:
				"En 2018, me mudé del Reino Unido a España para experimentar la vida en un país diferente. Como parte de la experiencia, dejé mi trabajo y ahora divido mi tiempo entre la enseñanza de inglés on line y el diseño y la autoedición de juegos de mesa.",
			favoriteSystems: [
				"Dragones & Mazmorras",
				"Traveller",
				"Vampiro: La Mascarada",
				"Mutants & Masterminds",
				"Hombre Lobo el Apocalipsis",
				"Mago la Ascensión",
				"Call of Cthulhu, todas las versiones",
			],
			otherSystems: ["Warhammer Fantasy", "Cortex+", "Rolemaster", "Fate"],
			tagline:
				"A lo largo de los años, he sido Director de Juego de docenas de sistemas y he jugado docenas más. Si quieres practicar tu inglés mientras juegas, estarás en buenas manos.",
			icon: faChessKnight,
		},
	];

	return (
		<main className="bg-black text-white">
			{/* Hero Section */}
			<section
				className="relative py-32 bg-cover bg-center"
				style={{ backgroundImage: "url('/images/contacto_header.webp')" }}
			>
				<div className="absolute inset-0 bg-black/70"></div>
				<div className="container mx-auto px-4 max-w-4xl relative z-10">
					<motion.h1
						className="font-amarante text-5xl md:text-7xl mb-6 text-center text-white"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<span className="headerFont inline-block pb-2 border-b-2 border-red-700">¿Nos buscabas?</span>
					</motion.h1>

					<motion.p
						className="text-2xl text-gray-300 max-w-3xl mx-auto text-center mb-8"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<strong className="text-red-400">
							Nos has encontrado
							<br />
							<span className="text-xl italic text-gray-300">
								Los Amos del Calabozo son varios Directores de Juego con amplia experiencia, aquí puedes
								conocer un poco más sobre ellos
							</span>
						</strong>
					</motion.p>
				</div>
			</section>

			{/* Dungeon Masters Section */}
			<section className="py-16 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faDice} className="mr-3 text-red-500" />
						Maestros de la Narración
					</motion.h2>

					<div className="flex flex-col gap-12">
						{dungeomMasters.map((master, index) => (
							<motion.div
								key={master.id}
								id={master.id} // 👈 Add this line
								className="bg-gray-800 rounded-lg overflow-hidden border-l-4 border-red-700"
								initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{ once: true }}
								transition={{ delay: index * 0.2, duration: 0.6 }}
							>
								<div className="p-8">
									<div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6">
										<div className="w-40 h-40 rounded-full overflow-hidden border-4 border-red-700 shadow-lg shadow-red-900/50 flex-shrink-0">
											<img
												src={master.avatar || `/images/dm-${master.id}.webp`}
												alt={`Imagen de ${master.name}`}
												className="w-full h-full object-cover"
											/>
										</div>
										<div>
											<div className="flex items-center mb-2">
												<div className="bg-red-600 p-3 rounded-full mr-3">
													<FontAwesomeIcon icon={master.icon} className="text-white text-xl" />
												</div>
												<div>
													<h3 className="text-3xl font-amarante headerFont text-red-400">{master.name}</h3>
													<p className="text-gray-300">{master.title}</p>
												</div>
											</div>
										</div>
									</div>

									<div className="space-y-4">
										<p className="text-gray-300">{master.description}</p>

										{master.background && <p className="text-gray-300">{master.background}</p>}

										<div>
											<h4 className="text-xl font-amarante text-red-300 mb-2">
												<FontAwesomeIcon icon={faDragon} className="mr-2" />
												Sistemas más dirigidos:
											</h4>
											<ul className="list-none grid grid-cols-1 md:grid-cols-2 gap-2">
												{master.favoriteSystems.map((system, idx) => (
													<li key={idx} className="text-gray-300">
														<span className="text-red-400">•</span> <em>{system}</em>
													</li>
												))}
											</ul>
										</div>

										<div>
											<h4 className="text-xl font-amarante text-red-300 mb-2">
												<FontAwesomeIcon icon={faBook} className="mr-2" />
												También dirige:
											</h4>
											<ul className="list-none grid grid-cols-1 md:grid-cols-2 gap-2">
												{master.otherSystems.map((system, idx) => (
													<li key={idx} className="text-gray-300">
														<span className="text-red-400">•</span> <em>{system}</em>
													</li>
												))}
											</ul>
										</div>

										{master.additionalInfo && <p className="text-gray-300 mt-4">{master.additionalInfo}</p>}

										<div className="bg-gray-700 p-4 rounded-lg mt-4">
											<p className="text-gray-200 italic">"{master.tagline}"</p>
										</div>
									</div>
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* Main Contact Methods */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<FontAwesomeIcon icon={faScroll} className="mr-3 text-red-500" />
						Formas de Invocación
					</motion.h2>

					<motion.div
						className="grid grid-cols-1 md:grid-cols-2 gap-8"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.5, duration: 0.6 }}
					>
						{contactMethods.map((method) => (
							<motion.div
								key={method.id}
								className="bg-gray-800 rounded-lg p-8 border-l-4 border-red-700 flex flex-col"
								whileHover={{ scale: 1.02, borderColor: "#f87171" }}
							>
								<div className="flex items-center mb-4">
									<div className="bg-red-600 p-4 rounded-full mr-4">
										<FontAwesomeIcon icon={method.icon} className="text-white text-2xl" />
									</div>
									<h3 className="text-2xl font-amarante headerFont text-red-400">{method.title}</h3>
								</div>
								<p className="text-gray-300 mb-6 flex-grow">{method.description}</p>
								<motion.a
									href={method.link}
									className={`${method.color} text-white px-6 py-3 rounded-lg font-bold text-center transition-colors`}
									whileHover={method.hoverEffect}
									whileTap={{ scale: 0.98 }}
									target={method.id === "whatsapp" ? "_blank" : ""}
									rel={method.id === "whatsapp" ? "noopener noreferrer" : ""}
								>
									<FontAwesomeIcon
										icon={method.id === "email" ? method.icon : method.icon}
										className="mr-2"
									/>
									{method.action}
								</motion.a>
							</motion.div>
						))}
					</motion.div>

					{/* Email Address Display */}
					<motion.div
						className="mt-8 sm:mt-10 md:mt-12 bg-gray-800 rounded-lg p-4 sm:p-6 md:p-8 text-center relative"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.8, duration: 0.6 }}
					>
						<h3 className="text-xl sm:text-2xl font-amarante headerFont text-red-400 mb-4">
							Nuestro Pergamino Oficial
						</h3>
						<motion.div
							className="text-lg sm:text-2xl md:text-4xl font-bold text-white bg-gray-700 py-3 px-2 sm:px-4 rounded-lg select-all cursor-pointer"
							whileHover={{
								backgroundColor: "#3b82f6",
								color: "white",
								transition: { duration: 0.3 },
							}}
							onClick={() => {
								navigator.clipboard.writeText("somos@losamosdelcalabozo.com");
								setShowCopyNotification(true);
								setTimeout(() => setShowCopyNotification(false), 2000);
							}}
						>
							somos@losamosdelcalabozo.com
						</motion.div>
						<p className="text-gray-400 mt-2 text-xs sm:text-sm italic">
							Haz click para copiar a tu grimorio de hechizos (portapapeles)
						</p>
						<AnimatePresence>
							{showCopyNotification && (
								<motion.div
									className="bg-green-600 text-white text-sm py-2 px-4 rounded-md absolute left-1/2 transform -translate-x-1/2 mt-2"
									initial={{ opacity: 0, y: -10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0 }}
								>
									Email copiado al portapapeles
								</motion.div>
							)}
						</AnimatePresence>
					</motion.div>
				</div>
			</section>

			{/* Easter Egg */}
			<section className="py-4 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="text-center opacity-30 hover:opacity-100 transition-opacity cursor-pointer"
						whileHover={{ scale: 1.01 }}
						whileTap={{ scale: 0.99 }}
					>
						<p className="text-sm text-gray-500 italic">
							Se dice que quien escucha el sonido de los dados rodando en la oscuridad, pronto será llamado a
							una gran aventura...
						</p>
					</motion.div>
				</div>
			</section>
		</main>
	);
};

export default AboutPage;
